import axiosInstance from '../../../services/axiosConfig';

// ✨ axiosInstance already has the base URL configured

// ✨ Get company ID from localStorage
const getCompanyId = () => {
  return localStorage.getItem('temp_com_id') || 3;
};

const widgetService = {
  // Get all custom widgets for a company
  async getCustomWidgets(companyId, includePublic = true) {
    try {
      companyId = companyId || getCompanyId();
      const response = await axiosInstance.get(`/scada/widgets`, {
        params: {
          companyId,
          includePublic
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching custom widgets:', error);
      return []; // Return empty array instead of throwing
    }
  },

  // Get widget by ID
  async getCustomWidget(widgetId) {
    try {
      const response = await axiosInstance.get(`/scada/widgets/${widgetId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching custom widget:', error);
      return null;
    }
  },

  // Create new custom widget
  async createCustomWidget(widgetData) {
    try {
      // Ensure widgetConfig is valid JSON string
      if (typeof widgetData.widgetConfig === 'object') {
        widgetData.widgetConfig = JSON.stringify(widgetData.widgetConfig);
      }

      // Add companyId if not present
      if (!widgetData.companyId) {
        widgetData.companyId = getCompanyId();
      }

      const response = await axiosInstance.post(`/scada/widgets`, widgetData);
      return response.data;
    } catch (error) {
      console.error('Error creating custom widget:', error);
      throw error;
    }
  },

  // Update existing custom widget
  async updateCustomWidget(widgetId, widgetData) {
    try {
      // Ensure widgetConfig is valid JSON string
      if (typeof widgetData.widgetConfig === 'object') {
        widgetData.widgetConfig = JSON.stringify(widgetData.widgetConfig);
      }

      const response = await axiosInstance.put(`/scada/widgets/${widgetId}`, widgetData);
      return response.data;
    } catch (error) {
      console.error('Error updating custom widget:', error);
      throw error;
    }
  },

  // Delete custom widget
  async deleteCustomWidget(widgetId) {
    try {
      const response = await axiosInstance.delete(`/scada/widgets/${widgetId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting custom widget:', error);
      throw error;
    }
  },

  // Get widget categories
  getWidgetCategories() {
    return [
      'indicators', 'controls', 'charts', 'shapes', 'display', 'input', 'custom', 'general'
    ];
  },

  // Get available widget types
  getWidgetTypes() {
    return [
      'text-label', 'text-box', 'button', 'led-indicator', 'progress-bar',
      'gauge', 'image', 'toggle-switch', 'trend-chart', 'combo-box',
      'box', 'line', 'circle'
    ];
  },

  // Parse widget configuration safely
  parseWidgetConfig(configString) {
    try {
      return typeof configString === 'string' ? JSON.parse(configString) : configString;
    } catch (error) {
      return {};
    }
  },

  // Validate widget configuration
  validateWidgetConfig(widgetType, config) {
    // Add validation logic based on widget type
    // This can be expanded to validate specific properties for each widget type
    try {
      const parsedConfig = this.parseWidgetConfig(config);
      return { valid: true, config: parsedConfig };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  },

  // Get default configuration for widget type
  getDefaultConfiguration(widgetType) {
    const defaults = {
      'text-label': {
        text: 'Sample Text',
        fontSize: '14px',
        textAlign: 'left',
        fontWeight: 'normal',
        fontFamily: 'inherit',
        backgroundColor: 'transparent',
        foreColor: '#333'
      },
      'text-box': {
        value: '',
        placeholder: 'Enter text...',
        multiline: false,
        rows: 3,
        readOnly: false,
        fontSize: '14px',
        backgroundColor: 'white',
        foreColor: '#333',
        borderWidth: 1,
        borderColor: '#ddd'
      },
      'button': {
        text: 'Button',
        variant: 'contained',
        disabled: false,
        backgroundColor: '#1976d2',
        foreColor: 'white',
        actionType: 'tag',
        tagOperation: 'toggle'
      },
      'led-indicator': {
        state: 'active',
        color: '#4caf50',
        offColor: '#cccccc',
        size: 24,
        label: '',
        blink: false,
        backgroundColor: 'transparent',
        foreColor: '#4caf50'
      },
      'progress-bar': {
        value: 50,
        min: 0,
        max: 100,
        showValue: true,
        vertical: false,
        thickness: 4,
        color: '#1976d2',
        backgroundColor: '#e0e0e0',
        foreColor: '#1976d2'
      },
      'gauge': {
        value: 0,
        min: 0,
        max: 100,
        unit: '%',
        title: '',
        startAngle: -90,
        endAngle: 90,
        showZones: true,
        normalZoneEnd: 60,
        warningZoneEnd: 80,
        normalColor: '#4caf50',
        warningColor: '#ff9800',
        dangerColor: '#f44336'
      },
      'toggle-switch': {
        label: '',
        size: 'medium',
        checked: false,
        disabled: false,
        color: '#1976d2'
      },
      'trend-chart': {
        tagIds: [],
        maxDataPoints: 50,
        showGrid: true,
        showLegend: true,
        dataMode: 'simulation',
        lineColors: ['#1976d2', '#dc004e', '#2e7d32', '#ed6c02', '#0288d1']
      },
      'button-menu': {
        options: [
          { label: 'ON', value: '1', color: '#ffffff', textColor: '#000' },
          { label: 'OFF', value: '0', color: '#ffffff', textColor: '#000' }
        ],
        tagId: null,
        colorMap: {},
        textColor: '#000000',
        defaultColor: '#ffffff',
        borderWidth: 2,
        borderColor: '#000000',
        borderRadius: 8,
        fontSize: 16
      },
      'set-value': {
        text: 'Set Value',
        tagId: null,
        placeholder: 'Enter value...',
        buttonText: 'Set',
        showButton: true,
        editable: true,
        borderWidth: 1,
        borderColor: '#000000',
        borderRadius: 4,
        foreColor: '#000000',
        textAlign: 'center',
        numberFormat: null
      },
      'combo-box': {
        options: [],
        placeholder: 'Select option...',
        variant: 'outlined'
      },
      'image': {
        src: '',
        alt: 'Image',
        fit: 'contain'
      },
      'box': {
        backgroundColor: '#f5f5f5',
        borderWidth: 1,
        borderColor: '#ddd',
        borderRadius: 4
      },
      'line': {
        borderWidth: 2,
        borderColor: '#333',
        direction: 'horizontal'
      },
      'circle': {
        backgroundColor: '#f5f5f5',
        borderWidth: 1,
        borderColor: '#ddd'
      }
    };

    return defaults[widgetType] || {};
  },

  // Create widget instance from template
  createWidgetInstance(templateWidget, overrides = {}) {
    const config = this.parseWidgetConfig(templateWidget.widgetConfig);
    
    return {
      id: `widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: templateWidget.widgetType,
      x: 100,
      y: 100,
      width: 120,
      height: 60,
      properties: {
        ...config,
        ...overrides
      },
      templateId: templateWidget.widgetId,
      templateName: templateWidget.widgetName
    };
  }
};

export default widgetService;